import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import CallToAction from '../components/sections/CallToAction';

const CaseStudiesContainer = styled.div``;

const HeroSection = styled.div`
  padding: 6rem 0 4rem;
  background-color: var(--primary-color);
  color: white;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  max-width: 800px;
  margin: 0 auto;
  color: rgba(255, 255, 255, 0.9);
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const CaseStudiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const CaseStudyCard = styled(motion.div)`
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: var(--shadow-lg);
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  border: 1px solid var(--border-color);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--shadow-xl);
  }
`;

const CaseStudyImage = styled.div`
  height: 180px;
  overflow: hidden;
  background-color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  position: relative;
  padding: 1.5rem;
  
  h3 {
    position: relative;
    z-index: 2;
    font-size: 1.5rem;
    text-align: center;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    color: white;
    background-color: transparent;
  }
`;

const CaseStudyContent = styled.div`
  padding: 2rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: white;
`;

const ClientName = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: var(--text-dark);
`;

const Industry = styled.div`
  font-size: 0.8rem;
  color: var(--primary-color);
  text-transform: uppercase;
  letter-spacing: 1px;
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background-color: var(--background-gray);
  border-radius: 20px;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const CaseSummary = styled.p`
  color: var(--text-medium);
  margin-bottom: 1.5rem;
  line-height: 1.6;
  font-size: 0.95rem;
`;

const Results = styled.div`
  margin-top: auto;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
`;

const ResultsTitle = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 1rem;
  color: var(--text-dark);
  
  &:after {
    content: '';
    position: absolute;
    bottom: -0.25rem;
    left: 0;
    width: 40px;
    height: 2px;
    background-color: var(--accent-color);
  }
`;

const ResultsList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 1.5rem;
  
  li {
    margin-bottom: 0.5rem;
    padding-left: 1.5rem;
    position: relative;
    font-size: 0.9rem;
    color: var(--text-light);
    
    &:before {
      content: '✓';
      position: absolute;
      left: 0;
      top: 0;
      color: var(--primary-color);
      font-weight: bold;
    }
  }
`;

const DetailButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: var(--primary-color);
  font-weight: 600;
  margin-top: auto;
  font-size: 0.9rem;
  
  &:after {
    content: '→';
    margin-left: 0.5rem;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    color: var(--primary-dark);
    &:after {
      transform: translateX(5px);
    }
  }
`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { y: 30, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.5 }
  }
};

const CaseStudies = () => {
  const caseStudies = [
    {
      id: 1,
      client: 'Global Energy Trading Company',
      industry: 'Energy Trading',
      summary: 'Implemented a comprehensive position limits management solution for a global energy trading firm dealing with complex regulatory requirements across multiple jurisdictions.',
      image: '/case-studies/energy-trading.jpg',
      results: [
        '65% reduction in compliance-related incidents',
        'Real-time position monitoring across 12 global exchanges',
        'Automated regulatory reporting resulting in 40 hours saved per week',
        'Seamless integration with existing trading systems'
      ]
    },
    {
      id: 2,
      client: 'European Power Producer',
      industry: 'Power Generation',
      summary: 'Deployed data integration solution to consolidate trading data from multiple systems, providing a unified view of positions and risk for a major European power producer.',
      image: '/case-studies/power-producer.jpg',
      results: [
        '90% reduction in data reconciliation time',
        'Eliminated data silos across 5 different trading systems',
        'Improved decision-making through real-time consolidated reporting',
        'Reduced operational risk from manual data handling'
      ]
    },
    {
      id: 3,
      client: 'North American Gas Trading Firm',
      industry: 'Natural Gas',
      summary: 'Implemented a trade reconciliation solution for a major North American gas trading firm to automate the matching of trades across their ETRM system and broker platforms.',
      image: '/case-studies/gas-trading.jpg',
      results: [
        '99.8% reconciliation accuracy achieved',
        '75% reduction in reconciliation processing time',
        'Early identification of trade breaks, reducing settlement risk',
        'Full audit trail of all reconciliation activities'
      ]
    },
    {
      id: 4,
      client: 'Independent Commodity Trader',
      industry: 'Commodities',
      summary: 'Delivered a custom regulatory reporting solution for an independent commodity trader, ensuring compliance with EMIR, REMIT, and MiFID II regulations.',
      image: '/case-studies/commodity-trader.jpg',
      results: [
        '100% regulatory reporting compliance achieved',
        'Automated generation of required regulatory reports',
        'Early warning system for potential reporting issues',
        'Flexible framework adaptable to changing regulations'
      ]
    },
    {
      id: 5,
      client: 'Multinational Energy Corporation',
      industry: 'Integrated Energy',
      summary: 'Developed a comprehensive market risk management solution for a multinational energy corporation, enabling advanced risk analytics and stress testing.',
      image: '/case-studies/multinational.jpg',
      results: [
        'Enhanced risk visualization with interactive dashboards',
        'Implementation of advanced VaR methodologies',
        'Scenario analysis capabilities for market stress events',
        'Integration with global market data sources'
      ]
    },
    {
      id: 6,
      client: 'Regional Power Utility',
      industry: 'Utilities',
      summary: 'Implemented exchange connectivity solution for a regional power utility to streamline access to power exchanges and improve trade execution efficiency.',
      image: '/case-studies/utility.jpg',
      results: [
        'Direct connection to 8 major power exchanges',
        '50% reduction in trade booking time',
        'Real-time market data access for trading decisions',
        'Improved trade execution speeds and reliability'
      ]
    }
  ];

  return (
    <CaseStudiesContainer>
      <HeroSection>
        <div className="container">
          <HeroTitle>Case Studies</HeroTitle>
          <HeroSubtitle>
            See how leading energy and commodity trading firms have transformed their operations with Talgo solutions
          </HeroSubtitle>
        </div>
      </HeroSection>

      <Section>
        <CaseStudiesGrid
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {caseStudies.map((caseStudy) => (
            <CaseStudyCard key={caseStudy.id} variants={itemVariants}>
              <CaseStudyImage>
                <h3>{caseStudy.client}</h3>
              </CaseStudyImage>
              <CaseStudyContent>
                <Industry>{caseStudy.industry}</Industry>
                <ClientName>{caseStudy.client}</ClientName>
                <CaseSummary>{caseStudy.summary}</CaseSummary>
                <Results>
                  <ResultsTitle>Key Results</ResultsTitle>
                  <ResultsList>
                    {caseStudy.results.map((result, index) => (
                      <li key={index}>{result}</li>
                    ))}
                  </ResultsList>
                </Results>
                <DetailButton to={`/case-studies/${caseStudy.id}`}>Read Full Case Study</DetailButton>
              </CaseStudyContent>
            </CaseStudyCard>
          ))}
        </CaseStudiesGrid>
      </Section>
      
      <CallToAction />
    </CaseStudiesContainer>
  );
};

export default CaseStudies; 